import React, { useSyncExternalStore } from 'react';
import styled from 'styled-components';
import { fontMono, semantic, typeRole } from '@repo/design-tokens';

/**
 * A view's zoom, held apart from the step's state: a wheel tick changes it many times a second, and
 * as step state each change re-rendered the whole Observers step (measured: a 50 ms median frame
 * under wheel zoom, against 16.7 ms). Only the readout subscribes (FR28). One store for both docks —
 * the plan's (`ObserverStage`) and the picture's (`PictureDock`).
 */
export interface ZoomReadout {
  set: (percent: number) => void;
  get: () => number;
  subscribe: (listener: () => void) => () => void;
}
export const createZoomReadout = (initial = 100): ZoomReadout => {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    set: percent => {
      if (percent === value) return;
      value = percent;
      listeners.forEach(l => l());
    },
    get: () => value,
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

/** The zoom as *{n} %* in mono, named *Zoom {n} percent* (FR11), alone subscribed to the store. */
export const ZoomPercent: React.FC<{ zoom: ZoomReadout; className?: string }> = ({
  zoom,
  className,
}) => {
  const percent = useSyncExternalStore(zoom.subscribe, zoom.get);
  return (
    <Text className={className} role="img" aria-label={`Zoom ${percent} percent`}>
      {percent} %
    </Text>
  );
};

const Text = styled.span`
  min-width: 4ch;
  font-family: ${fontMono};
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  color: ${semantic.text.secondary};
`;
